import { useSnackbar } from '@Providers/useSnackbar';
import api from '@Services/api';
import { FlexLayout } from '@Styled/utilities';
import SearchIcon from '@mui/icons-material/Search';
import {
	IconButton,
	MenuItem,
	Pagination,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import useStandalonePageLoader from '../useStandalonePageLoader';
import StandaloneQuestionCard from './StandaloneQuestionCard';
import { QuestionsMeta, StandaloneQuestion } from './QuestionsAndAnswers.types';

type LectureOption = { id: number; name: string };
type CourseLike = {
	id: number;
	tabs?: Array<{ sections?: Array<{ courses?: LectureOption[] }> }>;
	unsectioned_lectures?: LectureOption[];
};

type Props = { course: CourseLike };

/**
 * The classroom Q&A flow on a standalone course: unanswered/answered split,
 * inline answering, per-lecture filter. Instructor portal only — the list and
 * every mutation ride /instructor/discussions/*, gated by the `discussions`
 * grant and owner-scoped server-side.
 */
const StandaloneQuestionsAndAnswers: React.FC<Props> = ({ course }) => {
	const { displaySnackbar } = useSnackbar();
	const { withLoading } = useStandalonePageLoader();
	const [view, setView] = useState<'unanswered' | 'answered'>('unanswered');
	const [questions, setQuestions] = useState<StandaloneQuestion[]>([]);
	const [meta, setMeta] = useState<QuestionsMeta>({});
	const [page, setPage] = useState(1);
	const [searchInput, setSearchInput] = useState('');
	const [query, setQuery] = useState('');
	const [lectureId, setLectureId] = useState<number | ''>('');

	const lectures = useMemo<LectureOption[]>(
		() =>
			(course.tabs || [])
				.flatMap((tab) => tab.sections || [])
				.flatMap((section) => section.courses || [])
				.concat(course.unsectioned_lectures || []),
		[course.tabs, course.unsectioned_lectures]
	);

	const load = useCallback(
		async (
			activePage: number,
			activeView: 'unanswered' | 'answered',
			activeQuery: string,
			activeLecture: number | ''
		) => {
			try {
				await withLoading(async () => {
					const response = await api.post('discussions/fetch', {
						standalone_course_id: course.id,
						solved: activeView === 'answered',
						page: activePage,
						perPage: 10,
						query: activeQuery || undefined,
						...(activeLecture ? { course_id: activeLecture } : {}),
					});
					setQuestions(response.data.data as StandaloneQuestion[]);
					setMeta(response.data.meta || {});
				});
			} catch (error: any) {
				displaySnackbar(
					'error',
					error.response?.data?.message || 'Unable to load questions'
				);
			}
		},
		[course.id, displaySnackbar, withLoading]
	);

	useEffect(() => {
		load(page, view, query, lectureId);
	}, [load, page, view, query, lectureId]);

	const applySearch = () => {
		setPage(1);
		setQuery(searchInput.trim());
	};

	const refetch = () => load(page, view, query, lectureId);

	return (
		<Wrapper>
			<Controls>
				<FlexLayout alignItems="center" gridGap="1rem">
					<ToggleButtonGroup
						exclusive
						size="small"
						value={view}
						onChange={(_, value) => {
							if (!value) return;
							setPage(1);
							setView(value);
						}}
					>
						<ToggleButton value="unanswered">Unanswered</ToggleButton>
						<ToggleButton value="answered">Answered</ToggleButton>
					</ToggleButtonGroup>
					<TextField
						select
						size="small"
						label="Lecture"
						value={lectureId}
						sx={{ minWidth: '14rem' }}
						onChange={(e) => {
							setPage(1);
							setLectureId(e.target.value === '' ? '' : Number(e.target.value));
						}}
					>
						<MenuItem value="">All Lectures</MenuItem>
						{lectures.map((lecture) => (
							<MenuItem key={lecture.id} value={lecture.id}>
								{lecture.name}
							</MenuItem>
						))}
					</TextField>
				</FlexLayout>
				<FlexLayout alignItems="center">
					<TextField
						size="small"
						placeholder="Search questions"
						value={searchInput}
						onChange={(e) => setSearchInput(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === 'Enter') applySearch();
						}}
					/>
					<IconButton onClick={applySearch} aria-label="search">
						<SearchIcon />
					</IconButton>
				</FlexLayout>
			</Controls>
			{questions.length === 0 ? (
				<Empty>
					{view === 'unanswered'
						? 'No unanswered questions'
						: 'No answered questions yet'}
				</Empty>
			) : (
				questions.map((question) => (
					<StandaloneQuestionCard
						// remount when the row flips solved so the form re-seeds
						key={`${question.id}-${question.solved}`}
						question={question}
						defaultOpen={view === 'unanswered'}
						onChanged={refetch}
					/>
				))
			)}
			<FlexLayout justifyContent="center">
				<Pagination
					count={Number(meta.last_page || 1)}
					page={Number(meta.current_page || page)}
					onChange={(_, value) => setPage(value)}
				/>
			</FlexLayout>
		</Wrapper>
	);
};

const Wrapper = styled.div`
	padding: 0 2rem;
`;

const Controls = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 1rem;
	flex-wrap: wrap;
	margin-bottom: 0.5rem;
`;

const Empty = styled(Typography)`
	color: #a4a4a4;
	padding: 2rem 0;
	text-align: center;
`;

export default StandaloneQuestionsAndAnswers;
